import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Shield, 
  Heart, 
  Users, 
  Clock, 
  MapPin, 
  Camera, 
  Baby,
  Sun,
  Utensils,
  BookOpen,
  Star,
  ArrowRight,
  CheckCircle
} from 'lucide-react';

const Home = () => {
  const trustBadges = [
    "Licensed by Fraser Health",
    "Screen-Free Environment for Children", 
    "Secure Camera System (Internal Only)",
    "Certified ECE & ITE Staff",
    "120+ Minutes Active Play Daily"
  ];

  const policyHighlights = [
    {
      icon: Heart,
      title: "Respectful Guidance",
      description: "We use gentle, understanding approaches to guide children through emotions. No timeouts or punishment - just supportive guidance.",
      link: "/health-safety"
    },
    {
      icon: Utensils,
      title: "Nutrition Program",
      description: "Two healthy snacks provided daily. Parents send nut-free lunches. We support breastfeeding and special dietary needs.",
      link: "/program"
    },
    {
      icon: Shield,
      title: "Health & Safety",
      description: "Safe sleep checks every 10-15 minutes, comprehensive emergency plans, and maintaining a healthy environment for all.",
      link: "/health-safety"
    },
    {
      icon: Camera,
      title: "Transparent Monitoring",
      description: "Internal security cameras support supervision and safety. Parents can review footage when needed. No live streaming.",
      link: "/cameras"
    }
  ];

  const testimonials = [
    {
      name: "Sarah Chen",
      child: "Emma, 28 months",
      text: "The respectful guidance approach has helped Emma develop such emotional intelligence. She comes home happy and confident every day.",
      rating: 5
    },
    {
      name: "Michael Thompson", 
      child: "Lucas, 18 months",
      text: "We love the transparency with cameras and daily updates. The staff truly care about each child's individual development.",
      rating: 5
    },
    {
      name: "Priya Patel",
      child: "Arjun, 32 months", 
      text: "The nutrition program and outdoor play commitment are exactly what we wanted. Arjun has grown so much here!",
      rating: 5
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50 section-padding">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-4">
                <Badge className="bg-purple-500 text-white px-4 py-2 rounded-full">
                  Licensed Childcare
                </Badge>
                <h1 className="text-4xl md:text-5xl font-bold leading-tight">
                  <span className="text-purple-600">Welcome to</span>
                  <span className="block text-pink-500 mt-2">Maple Kids Academy</span>
                  <span className="block text-yellow-500 mt-2">Ages 0-36 Months</span>
                </h1>
                <p className="text-lg text-gray-700 leading-relaxed">
                  A warm, nurturing environment where your child feels safe, 
                  valued, and excited to explore and learn every day.
                </p>
              </div>

              {/* Simple Trust Badges */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-purple-400">
                  <p className="text-sm font-medium text-purple-600">Licensed</p>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-pink-400">
                  <p className="text-sm font-medium text-pink-600">Screen-Free</p>
                </div>
                <div className="bg-white p-3 rounded-lg shadow-sm border-l-4 border-yellow-400">
                  <p className="text-sm font-medium text-yellow-600">Safe & Secure</p>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-purple-500 hover:bg-purple-600 text-white px-6 py-3"
                >
                  <Link to="/contact">Book a Visit</Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 px-6 py-3"
                >
                  <Link to="/enroll">Join Our Family</Link>
                </Button>
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-3 gap-4 pt-6">
                <div className="text-center bg-white p-4 rounded-xl shadow-sm">
                  <Baby className="h-6 w-6 text-purple-500 mx-auto mb-2" />
                  <p className="text-xl font-bold text-purple-600">0-36</p>
                  <p className="text-xs text-gray-600">Months</p>
                </div>
                <div className="text-center bg-white p-4 rounded-xl shadow-sm">
                  <Clock className="h-6 w-6 text-pink-500 mx-auto mb-2" />
                  <p className="text-xl font-bold text-pink-600">120+</p>
                  <p className="text-xs text-gray-600">Active Play</p>
                </div>
                <div className="text-center bg-white p-4 rounded-xl shadow-sm">
                  <Sun className="h-6 w-6 text-yellow-500 mx-auto mb-2" />
                  <p className="text-xl font-bold text-yellow-600">100%</p>
                  <p className="text-xs text-gray-600">Screen-Free</p>
                </div>
              </div>
            </div>

            {/* Hero Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                <img 
                  src="/images/hero.png" 
                  alt="Maple Kids Academy building" 
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -bottom-4 -right-4 bg-yellow-400 text-purple-800 px-4 py-2 rounded-full shadow-lg">
                <p className="text-sm font-bold">Welcome to Maple Kids Academy!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-purple-600 mb-4">
              What Makes Us Special
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Every day at Maple Kids is filled with love, learning, and lots of fun!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {policyHighlights.map((highlight, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-200 border-2 border-transparent hover:border-purple-200">
                <CardHeader className="pb-3 text-center">
                  <div className="p-4 bg-gradient-to-br from-purple-100 to-pink-100 rounded-full w-fit mx-auto mb-3">
                    <highlight.icon className="h-7 w-7 text-purple-600" />
                  </div>
                  <CardTitle className="text-lg text-purple-700">{highlight.title}</CardTitle>
                </CardHeader>
                <CardContent className="pt-0 text-center">
                  <CardDescription className="text-gray-600 mb-4 leading-relaxed text-sm">
                    {highlight.description}
                  </CardDescription>
                  <Link 
                    to={highlight.link}
                    className="inline-flex items-center text-purple-600 font-medium text-sm hover:text-pink-600 transition-colors bg-purple-50 hover:bg-pink-50 px-3 py-2 rounded-full"
                  >
                    Learn more
                    <ArrowRight className="ml-1 h-3 w-3" />
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
          
          {/* Visual Gallery */}
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-purple-600 text-center mb-8">Happy Place in Action!</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-xl overflow-hidden shadow-lg border-4 border-white">
                <img 
                  src="/images/place1.png" 
                  alt="Cozy nap room" 
                  className="w-full h-32 object-cover hover:scale-105 transition-transform duration-200"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg border-4 border-white">
                <img 
                  src="/images/place2.png" 
                  alt="Colorful playroom" 
                  className="w-full h-32 object-cover hover:scale-105 transition-transform duration-200"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg border-4 border-white">
                <img 
                  src="/images/place3.png" 
                  alt="Outdoor play area" 
                  className="w-full h-32 object-cover hover:scale-105 transition-transform duration-200"
                />
              </div>
              <div className="rounded-xl overflow-hidden shadow-lg border-4 border-white">
                <img 
                  src="/images/place4.png" 
                  alt="Learning spaces" 
                  className="w-full h-32 object-cover hover:scale-105 transition-transform duration-200"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Daily Fun */}
      <section className="section-padding bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-purple-600 mb-6">
                A Fun-Filled Day at Maple Kids
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Every day is an adventure! Here's what your little one can expect.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-4 bg-white p-4 rounded-xl shadow-sm">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Sun className="h-5 w-5 text-yellow-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-purple-700">Morning Fun (7:00-11:30)</h4>
                    <p className="text-gray-600 text-sm">Play time, healthy snacks, and outdoor adventures</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-white p-4 rounded-xl shadow-sm">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <Utensils className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-purple-700">Lunch & Rest (12:00-3:00)</h4>
                    <p className="text-gray-600 text-sm">Yummy lunch and peaceful nap time</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4 bg-white p-4 rounded-xl shadow-sm">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <BookOpen className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-bold text-purple-700">Afternoon Play (3:00-6:00)</h4>
                    <p className="text-gray-600 text-sm">More fun activities until pickup time</p>
                  </div>
                </div>
              </div>

              <Button asChild className="mt-6 bg-purple-500 hover:bg-purple-600 text-white rounded-full">
                <Link to="/program">See Full Schedule</Link>
              </Button>
            </div>

            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-xl border-4 border-white">
                <img 
                  src="https://images.unsplash.com/photo-1637195141546-2469a5312504?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHwzfHx0b2RkbGVyc3xlbnwwfHx8fDE3NTYwMTU2NTd8MA&ixlib=rb-4.1.0&q=85" 
                  alt="Children playing and learning activities" 
                  className="w-full h-96 object-cover"
                />
              </div>
              <div className="absolute -top-4 -left-4 bg-purple-500 text-white px-4 py-2 rounded-full shadow-lg">
                <p className="text-sm font-bold">Learning & Fun!</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Happy Parents */}
      <section className="section-padding bg-white">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-purple-600 mb-4">
              Happy Parents, Happy Kids
            </h2>
            <p className="text-lg text-gray-600">
              See what families love about Maple Kids Academy
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-100 hover:border-purple-200 transition-all">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-gray-700 mb-4 italic leading-relaxed">
                    "{testimonial.text}"
                  </p>
                  <div className="border-t border-purple-200 pt-4">
                    <p className="font-bold text-purple-700">{testimonial.name}</p>
                    <p className="text-sm text-purple-600">Parent of {testimonial.child}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Visit Us */}
      <section className="section-padding bg-gradient-to-br from-purple-600 to-pink-500 text-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Come Visit Our Happy Place!
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0 text-yellow-300" />
                  <div>
                    <p className="font-bold text-lg">10681 248 St, Maple Ridge, BC</p>
                    <p className="text-purple-100">Easy parking for families</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <Clock className="h-5 w-5 mt-1 flex-shrink-0 text-green-300" />
                  <div>
                    <p className="font-bold text-lg">Monday - Friday: 7:00 AM - 5:00 PM</p>
                    <p className="text-purple-100">Closed weekends and holidays</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Link to="/contact">Book Your Visit</Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white/20 hover:bg-white/30 text-white border-2 border-white hover:border-yellow-300 font-bold px-8 py-4 rounded-full transition-all"
                >
                  <Link to="/enroll">Join Our Family</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30">
                <h3 className="text-2xl font-bold mb-6 text-center">
                  Why Choose Maple Kids?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Small groups, big love</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                      <Shield className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Licensed & insured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-pink-400 rounded-full flex items-center justify-center">
                      <Heart className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Every child is welcome</span>
                  </div>
                </div>
                
                <div className="mt-6 text-center bg-white/20 p-4 rounded-xl">
                  <p className="text-sm font-medium">Ready to join our family?</p>
                  <p className="text-xs text-purple-100 mt-1">Tours available by appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;